import { useCallback } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { FileWarning } from 'lucide-react'

import type { ListOrdersParams } from '@/lib/api/sales-order-api'
import { ListPageHeader } from '@/components/common/ListPageHeader'
import { LIST_CARD_TITLE_CLASS, ListCard } from '@/components/common/ListCard'
import { PaymentStatusBadge } from '@/components/sections/payment-status-badge'
import { SLABadge } from '@/components/sla-badge'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useClampPage } from '@/hooks/use-clamp-page'
import { useSalesOrders } from '@/hooks/use-sales-order-query'
import { formatDateTimeShort, formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/logistics/vtp-overdue',
)({
    validateSearch: (search: Record<string, unknown>): VtpOverdueSearch => ({
        tab: search.tab === 'return' ? 'return' : 'overdue',
        page: pageFromSearch(search.page),
        awaiting_page: pageFromSearch(search.awaiting_page),
        returning_page: pageFromSearch(search.returning_page),
    }),
    component: VtpOverduePage,
})

const PER_PAGE = 20
const COLUMN_COUNT = 7

type VtpOverdueTab = 'overdue' | 'return'
// Mỗi danh sách một khoá trang trên URL — như màn Đơn hàng: F5, Back hay gửi
// link vẫn mở đúng trang. Trang 1 không ghi lên URL.
type PageKey = 'page' | 'awaiting_page' | 'returning_page'
type VtpOverdueSearch = { tab: VtpOverdueTab } & Partial<
    Record<PageKey, number>
>

const pageFromSearch = (raw: unknown) =>
    typeof raw === 'number' && Number.isInteger(raw) && raw > 1
        ? raw
        : undefined
type CarrierReturnFilter = NonNullable<ListOrdersParams['carrier_return']>

// Đơn "đang giao" quá hạn mà VTP THỰC SỰ đang cầm: carrier_tracked lọc theo vận
// đơn đã được webhook báo lấy hàng, không theo nhãn đơn vị vận chuyển — nhãn có
// thể ghi VTP cho đơn chỉ bấm tay, hoặc ghi "Tự giao" cho đơn đã đẩy sang VTP.
//
// Đơn VTP đang giữ chờ shop quyết hoàn/giao lại (505) hoặc đang chở về (502)
// không phải VTP giao chậm, nên không nằm ở tab quá hạn mà ở tab "Hoàn hàng"
// (M-15 r3). 505 cần shop bấm Duyệt hoàn / Giao lại ở trang chi tiết đơn.
function VtpOverduePage() {
    const navigate = useNavigate({ from: '/logistics/vtp-overdue' })
    const { tab } = Route.useSearch()
    // replace: đổi tab không nhồi thêm history.
    const setTab = (t: VtpOverdueTab) =>
        void navigate({ search: { tab: t }, replace: true })

    return (
        <div className="space-y-4 page-pad">
            <ListPageHeader
                icon={<FileWarning className="size-5 shrink-0" />}
                title={m.vtp_overdue_title()}
                subtitle={m.vtp_overdue_subtitle()}
            />
            <Tabs
                value={tab}
                onValueChange={(v) => setTab(v as VtpOverdueTab)}
                className="space-y-4"
            >
                <TabsList>
                    <TabsTrigger value="overdue">
                        {m.vtp_overdue_tab_overdue()}
                    </TabsTrigger>
                    <TabsTrigger value="return">
                        {m.vtp_overdue_tab_return()}
                    </TabsTrigger>
                </TabsList>
                <TabsContent value="overdue">
                    <VtpOrderList
                        pageKey="page"
                        carrierReturn="excluded"
                        overdueOnly
                        emptyText={m.vtp_overdue_empty()}
                    />
                </TabsContent>
                <TabsContent value="return" className="space-y-6">
                    <section className="space-y-2">
                        <h2 className="text-base font-semibold">
                            {m.vtp_return_awaiting_title()}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            {m.vtp_return_awaiting_hint()}
                        </p>
                        <VtpOrderList
                            pageKey="awaiting_page"
                            carrierReturn="awaiting_decision"
                            overdueOnly={false}
                            emptyText={m.vtp_return_awaiting_empty()}
                        />
                    </section>
                    <section className="space-y-2">
                        <h2 className="text-base font-semibold">
                            {m.vtp_return_returning_title()}
                        </h2>
                        <p className="text-sm text-muted-foreground">
                            {m.vtp_return_returning_hint()}
                        </p>
                        <VtpOrderList
                            pageKey="returning_page"
                            carrierReturn="returning"
                            overdueOnly={false}
                            emptyText={m.vtp_return_returning_empty()}
                        />
                    </section>
                </TabsContent>
            </Tabs>
        </div>
    )
}

function VtpOrderList({
    carrierReturn,
    overdueOnly,
    emptyText,
    pageKey,
}: {
    pageKey: PageKey
    carrierReturn: CarrierReturnFilter
    overdueOnly: boolean
    emptyText: string
}) {
    const navigate = useNavigate({ from: '/logistics/vtp-overdue' })
    const search = Route.useSearch()
    const page = search[pageKey] ?? 1
    const setPage = (next: number) =>
        void navigate({
            search: (prev) => ({
                ...prev,
                [pageKey]: next > 1 ? next : undefined,
            }),
        })
    const { data, isLoading } = useSalesOrders({
        status: 'in_delivery',
        sla_state: overdueOnly ? 'overdue' : undefined,
        carrier_tracked: '1',
        carrier_return: carrierReturn,
        page,
        per_page: PER_PAGE,
    })
    const orders = data?.data ?? []
    const meta = data?.meta
    // Đơn rời danh sách liên tục (giao xong, chuyển hoàn) nên trang đang xem có
    // thể vượt quá số trang mới — về trang cuối, thay chỗ trong lịch sử.
    useClampPage(
        page,
        meta?.total_pages,
        useCallback(
            (last: number) =>
                void navigate({
                    search: (prev) => ({
                        ...prev,
                        [pageKey]: last > 1 ? last : undefined,
                    }),
                    replace: true,
                }),
            [navigate, pageKey],
        ),
    )

    const empty = (
        <div className="py-8 text-center text-muted-foreground">
            {emptyText}
        </div>
    )
    const loading = (
        <div className="py-8 text-center text-muted-foreground">
            {m.common_loading()}
        </div>
    )
    const deadlineOf = (value?: string) =>
        value ? formatDateTimeShort(value) : '—'

    return (
        <div className="space-y-4">
            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
                {isLoading
                    ? loading
                    : orders.length === 0
                      ? empty
                      : orders.map((order) => (
                            <ListCard
                                key={order.id}
                                onClick={() =>
                                    void navigate({
                                        to: '/orders/$orderId',
                                        params: { orderId: order.id },
                                    })
                                }
                                title={
                                    <Link
                                        to="/orders/$orderId"
                                        params={{ orderId: order.id }}
                                        className={LIST_CARD_TITLE_CLASS}
                                    >
                                        {order.order_code}
                                    </Link>
                                }
                                subtitle={
                                    order.customer_phone
                                        ? `${order.customer_name} · ${order.customer_phone}`
                                        : order.customer_name
                                }
                                badge={
                                    <SLABadge
                                        deadlineAt={order.sla_deadline_at}
                                        breached={order.sla_breached}
                                        warningAt={order.sla_warning_at}
                                    />
                                }
                                metric={{
                                    label: m.order_total(),
                                    value: formatVnd(
                                        Math.round(order.total_amount),
                                    ),
                                }}
                                fields={[
                                    {
                                        label: m.vtp_overdue_province(),
                                        value: order.shipping_province || '—',
                                    },
                                    {
                                        label: m.vtp_overdue_deadline(),
                                        value: deadlineOf(
                                            order.sla_deadline_at,
                                        ),
                                    },
                                    {
                                        label: m.order_payment(),
                                        value: (
                                            <PaymentStatusBadge
                                                status={order.payment_status}
                                                short
                                            />
                                        ),
                                    },
                                ]}
                            />
                        ))}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block rounded-md border overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.order_code()}</TableHead>
                            <TableHead>{m.order_customer()}</TableHead>
                            <TableHead>{m.vtp_overdue_province()}</TableHead>
                            <TableHead>{m.order_total()}</TableHead>
                            <TableHead>{m.order_payment()}</TableHead>
                            <TableHead>{m.vtp_overdue_deadline()}</TableHead>
                            <TableHead>{m.order_sla_label()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {isLoading || orders.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={COLUMN_COUNT}>
                                    {isLoading ? loading : empty}
                                </TableCell>
                            </TableRow>
                        ) : (
                            orders.map((order) => (
                                <TableRow key={order.id}>
                                    <TableCell>
                                        <Link
                                            to="/orders/$orderId"
                                            params={{ orderId: order.id }}
                                            className="font-mono text-xs text-primary hover:underline"
                                        >
                                            {order.order_code}
                                        </Link>
                                    </TableCell>
                                    <TableCell>
                                        <div className="font-medium">
                                            {order.customer_name}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                            {order.customer_phone}
                                        </div>
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {order.shipping_province || '—'}
                                    </TableCell>
                                    <TableCell className="font-medium">
                                        {formatVnd(
                                            Math.round(order.total_amount),
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <PaymentStatusBadge
                                            status={order.payment_status}
                                            short
                                        />
                                    </TableCell>
                                    <TableCell className="text-sm">
                                        {deadlineOf(order.sla_deadline_at)}
                                    </TableCell>
                                    <TableCell>
                                        <SLABadge
                                            deadlineAt={order.sla_deadline_at}
                                            breached={order.sla_breached}
                                            warningAt={order.sla_warning_at}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </div>

            {meta && meta.total_pages > 1 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {m.order_total_count({ count: String(meta.total) })}
                    </span>
                    <div className="flex gap-2">
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page === 1}
                            onClick={() => setPage(page - 1)}
                        >
                            {m.common_pagination_prev()}
                        </Button>
                        <span className="flex items-center px-2">
                            {page} / {meta.total_pages}
                        </span>
                        <Button
                            size="sm"
                            variant="outline"
                            disabled={page >= meta.total_pages}
                            onClick={() => setPage(page + 1)}
                        >
                            {m.common_pagination_next()}
                        </Button>
                    </div>
                </div>
            )}
        </div>
    )
}
